import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plug, Play, FileBarChart, Trash2, Plus, CalendarClock, Mail } from "lucide-react";
import AppShell from "../components/AppShell";
import ConnectorIcon from "../components/ConnectorIcon";
import AutoFixToggle from "../components/AutoFixToggle";
import DashboardValueSummary from "../components/customer/DashboardValueSummary";
import ScanHistory from "../components/customer/ScanHistory";
import {
  ActionButton,
  CustomerInput,
  CustomerPanel,
  DisclosurePanel,
  EmptyState,
  StatusBadge,
} from "../components/customer/CustomerUI";
import { LoadingState, ErrorState } from "../components/LoadingState";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const statusTone = (status, enabled) => {
  if (!enabled) return "warning";
  if (status === "connected") return "success";
  if (status === "error") return "danger";
  return "neutral";
};

export function replaceConnectionById(connections, updatedConnection) {
  return connections.map((connection) => (
    connection.id === updatedConnection.id ? updatedConnection : connection
  ));
}

export function ConnectionNameEditor({ connection, onSaved }) {
  const [label, setLabel] = useState(connection.label);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (saving) return;
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      toast.error("Connection name is required");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.patch(`/connections/${connection.id}/label`, {
        label: trimmedLabel,
      });
      onSaved(data);
      toast.success("Connection name updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-ui-3 border-b border-border pb-ui-3">
      <label className="font-mono text-[10px] uppercase tracking-wider text-slate-500" htmlFor={`connection-name-${connection.id}`}>
        Connection name
      </label>
      <div className="mt-ui-2 flex gap-ui-2">
        <CustomerInput
          id={`connection-name-${connection.id}`}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={120}
          aria-label="Connection name"
        />
        <ActionButton onClick={save} size="compact" disabled={saving}>
          {saving ? "Saving…" : "Save name"}
        </ActionButton>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [scans, setScans] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const [c, s, i] = await Promise.all([
        api.get("/connections"),
        api.get("/scans"),
        api.get("/insights"),
      ]);
      setConnections(c.data);
      setScans(s.data);
      setInsights(i.data);
    } catch (e) { setLoadError(true); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runScan = async (id) => {
    try {
      const { data } = await api.post(`/connections/${id}/scan`);
      navigate(`/scans/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/connections/${id}`); toast.success("Connection removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateSchedule = async (connection, frequency) => {
    try {
      const enabled = frequency !== "manual";
      const { data } = await api.patch(`/connections/${connection.id}/schedule`, { enabled, frequency });
      setConnections((prev) => prev.map((item) => item.id === connection.id ? data : item));
      toast.success(enabled ? `Scan schedule set to ${frequency}` : "Scheduled scans paused");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateDigest = async (connection, field, checked) => {
    try {
      const prefs = {
        per_scan: field === "per_scan" ? checked : connection.digest_per_scan,
        weekly: field === "weekly" ? checked : connection.digest_weekly,
      };
      const { data } = await api.patch(`/connections/${connection.id}/digest`, prefs);
      setConnections((prev) => prev.map((item) => item.id === connection.id ? data : item));
      toast.success("Email preferences updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const scoreColor = (s) => (s >= 80 ? "text-health-good" : s >= 60 ? "text-health-warn" : "text-health-critical");
  const enabledConnection = connections.find((connection) => connection.plan_access_enabled);
  const latestScan = insights?.summary?.latest_scan ?? null;

  return (
    <AppShell>
      <CustomerPanel className="relative mb-ui-6 flex flex-wrap items-end justify-between gap-ui-5 bg-gradient-to-br from-white to-blue-50 p-ui-6 sm:p-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Workspace</div>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
            Welcome, {user?.name?.split(" ")[0] || "there"}
          </h1>
        </div>
        <ActionButton data-testid="connect-crm-cta" onClick={() => navigate("/connect")}
                      variant="accent">
          <Plus className="h-4 w-4" /> Connect a CRM
        </ActionButton>
      </CustomerPanel>

      {!loading && !loadError && (
        <DashboardValueSummary
          latestScan={latestScan}
          issuesCorrected={insights?.summary?.value_realization?.issues_corrected ?? 0}
          hasConnection={Boolean(enabledConnection)}
          onRunScan={
            enabledConnection ? () => runScan(enabledConnection.id) : undefined
          }
          onOpenScan={
            latestScan?.scan_id
              ? () => navigate(`/scans/${latestScan.scan_id}`)
              : undefined
          }
          onConnect={() => navigate("/connect")}
          onViewInsights={() => navigate("/insights")}
        />
      )}

      {/* Connections */}
      <section className="mb-10">
        <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Connected CRMs</h2>
        {loading ? (
          <LoadingState cards={3} rows={2} label="Loading CRM connections" />
        ) : loadError ? (
          <ErrorState message="We could not load your CRM workspace." onRetry={() => { setLoading(true); load(); }} />
        ) : connections.length === 0 ? (
          <CustomerPanel data-testid="empty-connections">
            <EmptyState
              icon={Plug}
              title="No CRM connected yet"
              description="Connect GoHighLevel, or try the Demo CRM with sample data to see a full Health Report."
              action={
                <ActionButton data-testid="empty-connect-button" onClick={() => navigate("/connect")}>
                  Connect a CRM
                </ActionButton>
              }
            />
          </CustomerPanel>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {connections.map((c) => (
              <CustomerPanel key={c.id} data-testid={`connection-card-${c.id}`} className="flex flex-col">
                <div className="flex items-start justify-between gap-ui-3 px-ui-5 pt-ui-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-customer border border-border bg-slate-50">
                      <ConnectorIcon name={c.connector_type === "demo" ? "FlaskConical" : "Network"} className="h-5 w-5 text-slate-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{c.label}</div>
                      <div className="font-mono text-[11px] text-slate-500">{c.connector_name}</div>
                    </div>
                  </div>
                  <StatusBadge tone={statusTone(c.status, c.plan_access_enabled)}>
                    {c.plan_access_enabled ? c.status : "Plan locked"}
                  </StatusBadge>
                </div>
                <div className="flex items-center gap-ui-2 px-ui-5 pb-ui-4 pt-ui-5">
                  <ActionButton data-testid={`run-scan-button-${c.id}`} onClick={() => runScan(c.id)}
                                disabled={!c.plan_access_enabled}
                                title={c.plan_access_reason || ""}
                                className="flex-1">
                    <Play className="h-3.5 w-3.5" /> Run Scan
                  </ActionButton>
                  {c.last_scan_id && (
                    <ActionButton data-testid={`view-report-button-${c.id}`} onClick={() => navigate(`/scans/${c.last_scan_id}`)}
                                  variant="secondary" size="compact">
                      <FileBarChart className="h-3.5 w-3.5" /> Report
                    </ActionButton>
                  )}
                </div>
                {!c.plan_access_enabled && <p className="px-ui-5 pb-ui-3 text-xs text-amber-700">{c.plan_access_reason}</p>}
                <div className={`px-ui-5 pb-ui-4 ${!c.plan_access_enabled ? "pointer-events-none opacity-50" : ""}`}>
                  <DisclosurePanel
                    summary="Connection settings"
                    description="Auto-Fix, scan schedule, and email reports"
                  >
                    {["owner", "admin"].includes(user?.organization_role) && (
                      <ConnectionNameEditor
                        connection={c}
                        onSaved={(updatedConnection) => setConnections((prev) => (
                          replaceConnectionById(prev, updatedConnection)
                        ))}
                      />
                    )}
                    <AutoFixToggle
                      connection={c}
                      showDigestPreferences={false}
                      onChange={(v) => setConnections((prev) => prev.map((x) => x.id === c.id ? { ...x, auto_fix_enabled: v } : x))}
                    />
                    <div className="mt-ui-3 border-t border-border pt-ui-3">
                      <label className="flex min-h-touch items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-slate-500" htmlFor={`scan-frequency-${c.id}`}>
                        <CalendarClock className="h-3.5 w-3.5" /> Scan automation
                      </label>
                      <select id={`scan-frequency-${c.id}`} data-testid={`scan-frequency-${c.id}`} value={c.schedule_enabled ? c.scan_frequency : "manual"}
                              onChange={(e) => updateSchedule(c, e.target.value)}
                              className="min-h-touch w-full rounded-customer border border-border bg-white px-ui-3 text-xs text-slate-700 outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2">
                        <option value="manual">Manual / paused</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      {c.next_scan_at && <div className="mt-ui-1 text-[11px] text-slate-400">Next: {new Date(c.next_scan_at).toLocaleString()}</div>}
                    </div>
                    <div className="mt-ui-3 border-t border-border pt-ui-3">
                      <div className="flex min-h-touch items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                        <Mail className="h-3.5 w-3.5" /> Email reports
                      </div>
                      <div className="grid gap-ui-2 sm:grid-cols-2">
                        <label className="flex min-h-touch items-center gap-ui-2 rounded-customer border border-slate-200 bg-white px-ui-3 text-xs text-slate-600">
                          <input type="checkbox" checked={c.digest_per_scan} onChange={(e) => updateDigest(c, "per_scan", e.target.checked)} className="h-4 w-4 accent-slate-900" /> Per scan
                        </label>
                        <label className="flex min-h-touch items-center gap-ui-2 rounded-customer border border-slate-200 bg-white px-ui-3 text-xs text-slate-600">
                          <input type="checkbox" checked={c.digest_weekly} onChange={(e) => updateDigest(c, "weekly", e.target.checked)} className="h-4 w-4 accent-slate-900" /> Weekly
                        </label>
                      </div>
                    </div>
                  </DisclosurePanel>
                </div>
                <div className="mt-auto flex justify-end border-t border-slate-200 bg-slate-50/60 px-ui-5 py-ui-3">
                  <ActionButton
                    data-testid={`delete-connection-button-${c.id}`}
                    onClick={() => remove(c.id)}
                    variant="destructive"
                    size="icon"
                    aria-label={`Remove CRM connection ${c.label}`}
                    title={`Remove CRM connection ${c.label}`}
                  >
                    <Trash2 />
                  </ActionButton>
                </div>
              </CustomerPanel>
            ))}
          </div>
        )}
      </section>

      <ScanHistory
        scans={scans}
        scoreColor={scoreColor}
        onOpen={(scanId) => navigate(`/scans/${scanId}`)}
      />
    </AppShell>
  );
}
